import * as React from "react";
import KEY from "/src/constants/keyboard-keydown-key";
import Label from "../label/Label";
import ErrorMessage from "../error-message/ErrorMessage";
import DropdownMenu from "../../dropdown-menu/DropdownMenu";

import ChevronRightIcon from "/src/svg/chevron-right.svg";
import HelpCircleIcon from "/src/svg/help-circle.svg";

import {BaseInput, InputType} from "/src/types";
import {css} from "/src/utils/utils";

import Icon from "../../icon/Icon";

import * as styles from "./split-dropdown.module.scss";

interface DropdownOption {
  label: string;
  value: string;
  selectedLabel?: string;
}

interface SplitDropdownProps extends BaseInput {
  defaultValue?: string;
  disableError?: boolean;
  inputRef?:
    | React.RefObject<HTMLInputElement>
    | React.ForwardedRef<HTMLInputElement>;
  max?: number;
  maxLength?: number;
  min?: number;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onInputChange?: (value: string) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type: InputType;
  onDropdownChange?: (str: string) => unknown;
  inputPlaceholder?: string;
  options: Array<DropdownOption>;
  transparentBg?: boolean;
  restrictWidth?: boolean;
  classes?: string;
  tooltipContent?: string;
}

export default function SplitDropdown(props: SplitDropdownProps) {
  const {
    darkMode = false,
    defaultValue = undefined,
    disabled = false,
    disableError = false,
    errorMessage = "",
    inputRef,
    label = "",
    max,
    maxLength,
    min,
    name,
    onBlur,
    onInputChange,
    onFocus,
    onKeyDown,
    inputPlaceholder = "",
    readOnly = false,
    required = false,
    status,
    transparentBg = false,
    type,
    value,

    placeholder = "",
    onDropdownChange,
    options,
    restrictWidth = false,
    classes,
    tooltipContent = undefined,
  } = props;

  const [isFocusedState, setIsFocusedState] = React.useState(false);

  const [selectedValue, setSelectedValue] = React.useState<string | undefined>(
    value
  );
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [focusedListItem, setFocusedListItem] = React.useState<number>(-1);

  const $root = React.useRef<null | HTMLDivElement>(null);

  const isInvalid = status === "error";
  const isValid = status === "success";

  const labelId = `${name}_label`;
  const triggerId = `${name}_trigger`;
  const itemListId = `${name}_list`;

  const onInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // if status is not error/success, set focus state
    if (!isInvalid && !isValid) {
      setIsFocusedState(true);
    }

    if (onFocus) {
      onFocus(e);
    }
  };

  const onInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocusedState(false);

    if (onBlur) {
      onBlur(e);
    }
  };

  React.useEffect(() => {
    if (!isOpen) {
      setFocusedListItem(-1);
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (value !== selectedValue) {
      setSelectedValue(value!);
    }
  }, [selectedValue, value]);

  /**
   * Returns label of option that matches provided value.
   * @param {string} val
   * @returns {string|null}
   */
  const getLabelByValue = (val?: string): string | null => {
    if (!options || val == null) {
      return null;
    }

    const temp: Array<DropdownOption> = options.filter(
      item => item.value === val
    );

    if (temp.length > 0) {
      return temp[0].selectedLabel != null &&
        temp[0].selectedLabel.trim() !== ""
        ? temp[0].selectedLabel
        : temp[0].label;
    }

    return null;
  };

  const onTriggerClick = () => {
    if (disabled || readOnly) {
      return;
    }

    setIsOpen(!isOpen);
  };

  const onTriggerFocus = () => {
    // if status is not error/success, set focus state
    if (!isInvalid && !isValid) {
      setIsFocusedState(true);
    }
  };

  const onTriggerKeydown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    switch (event.key) {
      case KEY.SPACE:
      case KEY.ENTER:
        event.preventDefault();
        setIsOpen(!isOpen);

        if (event.key === KEY.ENTER && focusedListItem !== -1) {
          selectValue(options[focusedListItem].value);
        }
        break;
      case KEY.ESC:
        setIsOpen(false);
        setFocusedListItem(-1);
        break;
      case KEY.UP:
        {
          event.preventDefault();
          const tempIdx = focusedListItem - 1;

          focusListItemByIdx(tempIdx);

          setIsOpen(true);
        }
        break;
      case KEY.DOWN:
        {
          event.preventDefault();
          const tempIdx = focusedListItem + 1;

          focusListItemByIdx(tempIdx);

          setIsOpen(true);
        }
        break;
      default:
        break;
    }
  };

  const onTriggerBlur = () => {
    if (!isOpen) {
      setIsFocusedState(false);
    }
  };

  const focusListItemByIdx = (idx: number) => {
    if (idx > -1 && idx < options.length) {
      setFocusedListItem(idx);
    }
  };

  const selectValue = (val: string) => {
    setIsOpen(!isOpen);
    setSelectedValue(val);

    if (onDropdownChange) {
      onDropdownChange(val);
    }
  };

  const onListItemClick = (val: string) => {
    selectValue(val);
  };

  const onListItemEnter = (idx: number) => {
    setFocusedListItem(idx);
  };

  const onListItemLeave = () => {
    setFocusedListItem(-1);
  };

  const handleInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (onInputChange) {
        onInputChange(event.target.value);
      }
    },
    [onInputChange]
  );

  return (
    <div
      className={css(
        darkMode ? styles.splitDropdownDark : "",
        disabled ? styles.splitDropdownDisabled : "",
        isFocusedState ? styles.splitDropdownFocused : "",
        isInvalid ? styles.splitDropdownError : "",
        isValid ? styles.splitDropdownSuccess : "",
        styles.splitDropdown__label,
        transparentBg ? styles.splitDropdownTransparent : "",
        classes!
      )}
    >
      <Label
        label={label}
        required={required}
        darkMode={darkMode}
        disabled={disabled}
        tooltipContent={tooltipContent}
        tooltipIcon={HelpCircleIcon}
      />
      <div className={styles.splitDropdown__inlineDropdown}>
        <div className={styles.splitDropdown__wrapper}>
          <input
            className={css(
              "p",
              styles.splitDropdown__input,
              isInvalid || isValid ? styles.splitDropdown__inputPadded : ""
            )}
            data-lpignore="true"
            defaultValue={defaultValue}
            disabled={disabled}
            max={max}
            maxLength={maxLength}
            min={min}
            name={name}
            onBlur={onInputBlur}
            onChange={handleInputChange}
            onFocus={onInputFocus}
            onKeyDown={onKeyDown}
            placeholder={inputPlaceholder}
            readOnly={readOnly}
            ref={inputRef}
            required={required}
            type={type}
          />
          <div
            className={css(
              darkMode ? styles.splitDropdownDark : "",
              transparentBg ? styles.splitDropdownTransparent : "",
              isFocusedState ? styles.splitDropdownFocus : "",
              isOpen ? styles.splitDropdownOpen : "",
              isInvalid ? styles.splitDropdownError : "",
              isValid ? styles.splitDropdownSuccess : "",
              disabled ? styles.splitDropdownDisabled : "",
              classes!
            )}
          >
            <div ref={$root} className={css(styles.splitDropdown__elWrapper)}>
              {/* TODO: aria-required not allowed by spec for buttons. we might have to refactor this component. */}
              <button
                aria-required={required}
                onKeyDown={onTriggerKeydown}
                onClick={onTriggerClick}
                onFocus={onTriggerFocus}
                onBlur={onTriggerBlur}
                className={css("p", styles.splitDropdown__btn)}
                type="button"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                aria-labelledby={`${labelId} ${triggerId}`}
                id={triggerId}
              >
                <span
                  className={css(
                    styles.splitDropdown__btnLbl,
                    selectedValue === ""
                      ? styles.splitDropdown__btnLblPlaceholder
                      : ""
                  )}
                >
                  {getLabelByValue(selectedValue) || placeholder}
                </span>
                <Icon
                  svg={ChevronRightIcon}
                  role="presentation"
                  classes={styles.splitDropdown__icon}
                />
              </button>
              <DropdownMenu
                classes={styles.splitDropdown__menu}
                bottomStyles={{
                  borderRadius: "0 0 4px 4px",
                  borderBottom: "2px solid",
                  borderBottomColor: "inherit",
                }}
                maxHeight={344} // approximately 10 lines
                offsetY={-4}
                open={isOpen}
                parent={$root}
                restrictWidth={restrictWidth}
                toggle={setIsOpen}
                topStyles={{
                  borderRadius: "4px 4px 0 0",
                  borderTop: "2px solid",
                  borderTopColor: "inherit",
                }}
              >
                <ul
                  className={styles.splitDropdown__list}
                  id={itemListId}
                  tabIndex={-1}
                  role="listbox"
                  aria-activedescendant={selectedValue}
                  aria-hidden={!isOpen}
                  aria-labelledby={labelId}
                >
                  {options.map((option: DropdownOption, i: number) => {
                    const selected = selectedValue === option.value;
                    const listIsFocus = i === focusedListItem;
                    return (
                      <li
                        onMouseEnter={() => onListItemEnter(i)}
                        onMouseLeave={onListItemLeave}
                        key={`${name}-${option.value}-${i}`}
                        onClick={() => onListItemClick(option.value)}
                        className={css(
                          styles.splitDropdown__item,
                          listIsFocus ? styles.splitDropdown__itemFocus : "",
                          selected ? styles.splitDropdown__itemSelected : ""
                        )}
                        id={option.value}
                        role="option"
                        aria-selected={selected}
                        aria-label={option.label}
                      >
                        {option.label}
                      </li>
                    );
                  })}
                </ul>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      {!disableError && (
        <ErrorMessage message={errorMessage} visible={isInvalid} />
      )}
    </div>
  );
}
